import { Box, CircularProgress, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { IInfluencer } from '../../redux/buzzbridge/types';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { BRAND_LUXE_BG, brandMeshBackground } from '../../pages/BrandDashboard/brandDashboardTokens';
import { PublicInfluencerProfileView } from './PublicInfluencerProfileView';
import { usePublicInfluencerProfile } from './usePublicInfluencerProfile';
import { getInfluencerAccountUserName } from './publicInfluencerProfileUtils';
import { ROUTES } from '../../types/global/routes.types';

export interface CreatorPublicProfileDrawerProps {
    open: boolean;
    onClose: () => void;
    /** Account userName for GET /influencers/public/:userName */
    userName?: string;
    /** Search-card snapshot while the public profile loads */
    initialInfluencer?: IInfluencer | null;
    onInvite?: () => void;
    inviteLabel?: string;
    invited?: boolean;
}

export function CreatorPublicProfileDrawer({
    open,
    onClose,
    userName: userNameProp,
    initialInfluencer,
    onInvite,
    inviteLabel = 'Invite to campaign',
    invited = false,
}: CreatorPublicProfileDrawerProps) {
    const userName = userNameProp || (initialInfluencer ? getInfluencerAccountUserName(initialInfluencer) : undefined);

    const { influencer, loading, error } = usePublicInfluencerProfile(
        open ? userName : undefined,
        open ? initialInfluencer : null,
    );

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 'min(50vw, 720px)' },
                    maxWidth: '100%',
                    ...brandMeshBackground(BRAND_LUXE_BG),
                    borderLeft: '1px solid rgba(255,255,255,0.08)',
                },
            }}>
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    p: 1,
                    bgcolor: 'rgba(14,14,14,0.9)',
                    backdropFilter: 'blur(8px)',
                }}>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ color: 'rgba(255,255,255,0.5)' }}
                    aria-label="Close profile">
                    <CloseIcon />
                </IconButton>
            </Box>

            {loading && !influencer && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress sx={{ color: '#ffc965' }} />
                </Box>
            )}

            {error && !influencer && (
                <Box sx={{ px: 3, py: 6, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff', mb: 1 }}>
                        Profile unavailable
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#adaaaa' }}>{error}</Typography>
                </Box>
            )}

            {influencer && (
                <PublicInfluencerProfileView
                    layout="drawer"
                    influencer={influencer}
                    onNameClick={
                        userName
                            ? () => {
                                  const handle = userName.startsWith('@') ? userName : `@${userName}`;
                                  const url = ROUTES.PUBLIC_CREATOR_PROFILE.replace(':userName', handle);
                                  window.open(url, '_blank', 'noopener,noreferrer');
                              }
                            : undefined
                    }
                    footerAction={
                        onInvite
                            ? {
                                  label: inviteLabel,
                                  onClick: onInvite,
                                  invited,
                              }
                            : undefined
                    }
                />
            )}
        </Drawer>
    );
}
